import { ActionBuilderActionTypes, StepTypes } from './enums';
import { CanvasWorkflowBuilderState } from 'src/slices/canvas-workflow-builder/workflowBuilderSlice';
import { getStore } from 'src/store/storeRef';
import { Action } from 'src/types/actionBuilderApiTypes';
import { ActionBuilderState } from 'src/types/actionBuilderReducerTypes';
import { ActionStepList } from 'src/types/workflowBuilderAPITypes';

/**
 * Redux-agnostic helper to check if a step is the entry step of a GRANULAR_ARTICLE_SUGGESTION_V2 action.
 * @param canvasActions - The canvas_action_id_to_action_component map
 * @param actions - Array of all actions
 * @param stepId - The step ID to check
 * @returns true if the step is a GRANULAR_ARTICLE_SUGGESTION_V2 entry step
 */
export const isEntryStepOfGranularArticleSuggestionV2FromCanvas = (
  canvasActions: Record<string, ActionStepList>,
  actions: Action[],
  stepId: string | undefined,
): boolean => {
  if (!stepId) {
    return false;
  }

  const granularArticleSuggestionActionId = actions.find(
    (action: Action) =>
      action.action_type ===
      ActionBuilderActionTypes.GRANULAR_ARTICLE_SUGGESTION_V2,
  )?.action_id;

  return Object.values(canvasActions).some(action => {
    return (
      action.entry_step_id === stepId &&
      action.action_id === granularArticleSuggestionActionId
    );
  });
};

/**
 * Legacy Redux-based version for backward compatibility.
 */
export const isEntryStepOfGranularArticleSuggestionV2 = (
  stepId: string | undefined,
): boolean => {
  const { actionBuilder, canvasWorkflowBuilder } = getStore().getState();
  const { actions } = actionBuilder as ActionBuilderState;
  const canvasActions = (canvasWorkflowBuilder as CanvasWorkflowBuilderState)
    .canvas_action_id_to_action_component;

  return isEntryStepOfGranularArticleSuggestionV2FromCanvas(
    canvasActions,
    actions,
    stepId,
  );
};

/**
 * Redux-agnostic helper to get the parent step ID (condition step) for a GRANULAR_ARTICLE_SUGGESTION_V2 action.
 * @param canvasActions - The canvas_action_id_to_action_component map
 * @param entryStepId - The entry step ID of the GRANULAR action
 * @returns The condition step ID (from transition_branches[0]), or the entry step ID as fallback
 */
export const getGranularArticleSuggestionV2ParentStepIdFromCanvas = (
  canvasActions: Record<string, ActionStepList>,
  entryStepId: string,
): string => {
  const actionIdKeys = Object.keys(canvasActions);

  const granularArticleSuggestionActionId =
    actionIdKeys.find(
      key => canvasActions[key]?.entry_step_id === entryStepId,
    ) || '';

  const transitionBranches =
    canvasActions[granularArticleSuggestionActionId]?.transition_branches;

  if (transitionBranches) {
    return transitionBranches[0]?.parent_step_id || entryStepId;
  }

  return entryStepId;
};

/**
 * Legacy Redux-based version for backward compatibility.
 */
export const getGranularArticleSuggestionV2ParentStepId = (
  stepId: string,
): string => {
  const state = getStore().getState() as {
    canvasWorkflowBuilder: CanvasWorkflowBuilderState;
  };
  const canvasActions =
    state.canvasWorkflowBuilder.canvas_action_id_to_action_component;

  return getGranularArticleSuggestionV2ParentStepIdFromCanvas(
    canvasActions,
    stepId,
  );
};

export const isGranularArticleSuggestionV2Deletable = (
  stepsWithinAction: string[],
): boolean => {
  const state = getStore().getState() as {
    canvasWorkflowBuilder: CanvasWorkflowBuilderState;
  };
  const steps = state.canvasWorkflowBuilder.steps;

  const conditionStepId =
    stepsWithinAction.find(
      stepId => steps[stepId].step_type === StepTypes.CONDITION,
    ) || '';
  const conditionStep = steps[conditionStepId];

  return (
    conditionStep.transitions[0].step_id === null ||
    conditionStep.transitions[1].step_id === null
  );
};
